import type { ReactNode } from "react";
import { landingThemeVars } from "../theme/landingTheme";

type IconName = "phone" | "calendar" | "clipboard" | "shield" | "bolt";

type Step = {
  title: string;
  description: string;
  icon: IconName;
};

type Benefit = {
  title: string;
  description: string;
  icon: IconName;
};

type Testimonial = {
  quote: string;
  name: string;
  role: string;
};

type Faq = {
  question: string;
  answer: string;
};

const navLinks = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "Benefits", href: "#benefits" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
] as const;

const steps: Step[] = [
  {
    title: "Capture Every Request",
    description:
      "Calls, texts, and web inquiries are logged instantly so no booking or renewal request slips through.",
    icon: "phone",
  },
  {
    title: "Trigger Smart Workflows",
    description:
      "Event-driven automations route scheduling, reminders, and follow-up tasks without manual copy/paste.",
    icon: "bolt",
  },
  {
    title: "Handoff to Staff",
    description:
      "When nuance is needed, VetCan hands off to your team with full context and timestamped notes.",
    icon: "clipboard",
  },
  {
    title: "Close the Loop",
    description:
      "Renewals and booking exceptions stay visible until resolved, with reminders and accountability built in.",
    icon: "calendar",
  },
];

const benefits: Benefit[] = [
  {
    title: "More Completed Bookings + Renewals",
    description:
      "Keep high-value follow-up work from falling through cracks, even during peak front desk hours.",
    icon: "calendar",
  },
  {
    title: "Less Phone Tag",
    description:
      "Automated intake captures intent first so your staff spends less time in repetitive back-and-forth calls.",
    icon: "phone",
  },
  {
    title: "Audit Trail + Accountability",
    description:
      "Every workflow action is logged with timestamps so leadership can trace execution and outcomes clearly.",
    icon: "shield",
  },
  {
    title: "Staff Handoff When Needed",
    description:
      "Operational requests stay automated until a human is needed, then context is handed to staff immediately.",
    icon: "clipboard",
  },
];

const testimonials: Testimonial[] = [
  {
    quote:
      "We stopped losing renewals in voicemail. VetCan gave us one operational queue and a clear owner for every follow-up.",
    name: "Jordan M.",
    role: "Practice Manager, Palm Grove Vet Clinic",
  },
  {
    quote:
      "Our front desk got hours back every week. Patients get faster responses, and our team only jumps in when needed.",
    name: "Renee T.",
    role: "Clinic Director, Harbor Animal Care",
  },
  {
    quote:
      "The audit log is huge for accountability. We can see exactly when reminders fired, when staff took over, and what closed.",
    name: "Dr. Lewis K.",
    role: "Owner, Eastside Veterinary Group",
  },
];

const faqs: Faq[] = [
  {
    question: "Does VetCan provide medical advice?",
    answer:
      "No. VetCan is for operational assistance only, including first-contact booking, scheduling support, operational follow-up, and renewal reminders. It does not provide diagnosis or medical advice.",
  },
  {
    question: "How do you handle privacy and operational logging?",
    answer:
      "Workflows are tracked through audited operational events with timestamps and ownership history. The model is PHI-safe by design and focused on operational context.",
  },
  {
    question: "How does staff handoff work?",
    answer:
      "When a request needs a person, VetCan flags the interaction, packages context, and routes it to designated staff so they can continue without starting over.",
  },
  {
    question: "Can VetCan support scheduling and reminder flows?",
    answer:
      "Yes. VetCan supports first-contact appointment booking, payment-safe completion steps, reminder nudges, booking-exception follow-up, and renewal reminder workflows.",
  },
  {
    question: "How long does setup usually take?",
    answer:
      "Most clinics can launch an initial workflow in days, then expand in phases as routing and handoff preferences are validated.",
  },
  {
    question: "What integrations are supported?",
    answer:
      "VetCan can connect at a high level to automation tooling such as n8n plus SMS/voice channels and scheduling systems based on your current stack.",
  },
  {
    question: "What is the expected business impact?",
    answer:
      "Clinics typically use VetCan to reduce booking and renewal misses, helping recover roughly $50k/year in payroll-equivalent operational leakage.",
  },
];

function IconPhone() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <path
        fill="currentColor"
        d="M6.6 10.8a15.2 15.2 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25c1 .35 2.1.55 3.2.55a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.5 21 3 13.5 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.1.2 2.2.55 3.2a1 1 0 0 1-.25 1z"
      />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <path
        fill="currentColor"
        d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1.5A2.5 2.5 0 0 1 22 6.5v12A2.5 2.5 0 0 1 19.5 21h-15A2.5 2.5 0 0 1 2 18.5v-12A2.5 2.5 0 0 1 4.5 4H6V3a1 1 0 0 1 1-1zm13 8H4v8.5c0 .28.22.5.5.5h15a.5.5 0 0 0 .5-.5zm-.5-4h-15a.5.5 0 0 0-.5.5V8h16V6.5a.5.5 0 0 0-.5-.5z"
      />
    </svg>
  );
}

function IconClipboard() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <path
        fill="currentColor"
        d="M9 2a2 2 0 0 0-2 2H6a2 2 0 0 0-2 2v13a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V6a2 2 0 0 0-2-2h-1a2 2 0 0 0-2-2zm0 2h6v2H9zm-2 4h10a1 1 0 1 1 0 2H7a1 1 0 1 1 0-2zm0 4h10a1 1 0 1 1 0 2H7a1 1 0 1 1 0-2z"
      />
    </svg>
  );
}

function IconShield() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <path
        fill="currentColor"
        d="m12 2 8 3v6c0 5.25-3.4 9.74-8 11-4.6-1.26-8-5.75-8-11V5zm4.6 6.2a1 1 0 0 0-1.4 0L11 12.4l-2.2-2.2a1 1 0 0 0-1.4 1.4l2.9 2.9a1 1 0 0 0 1.4 0l4.9-4.9a1 1 0 0 0 0-1.4z"
      />
    </svg>
  );
}

function IconBolt() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <path fill="currentColor" d="M13 2 4 13h6l-1 9 9-11h-6z" />
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
      <path
        fill="currentColor"
        d="m13.2 4.8 6.4 6.4a1.1 1.1 0 0 1 0 1.6l-6.4 6.4a1.1 1.1 0 1 1-1.6-1.6l4.5-4.5H5.5a1.1 1.1 0 1 1 0-2.2h10.6l-4.5-4.5a1.1 1.1 0 0 1 1.6-1.6z"
      />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4 text-[var(--landing-brand)] transition group-open:rotate-180"
    >
      <path
        fill="currentColor"
        d="m12 15.25-5.25-5.25a1 1 0 0 1 1.4-1.4L12 12.42l3.85-3.82a1 1 0 1 1 1.4 1.4z"
      />
    </svg>
  );
}

function iconFor(name: IconName) {
  const map: Record<IconName, ReactNode> = {
    phone: <IconPhone />,
    calendar: <IconCalendar />,
    clipboard: <IconClipboard />,
    shield: <IconShield />,
    bolt: <IconBolt />,
  };
  return map[name];
}

export default function LandingPage() {
  return (
    <div
      style={landingThemeVars}
      className="bg-[var(--landing-page-bg)] text-[var(--landing-ink)] [font-family:var(--landing-font-family)]"
    >
      <div className="bg-[var(--landing-hero-to)] text-[var(--landing-on-dark-soft)]">
        <div className="mx-auto grid max-w-6xl gap-2 px-4 py-2 text-xs sm:grid-cols-3 sm:gap-4 sm:text-sm">
          <div className="flex items-center gap-2">
            <span className="text-[var(--landing-brand-soft)]">
              <IconPhone />
            </span>
            <span>Clinic Front Desk Line: (800) 803-8525</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[var(--landing-brand-soft)]">
              <IconCalendar />
            </span>
            <span>24/7 automated workflow coverage</span>
          </div>
          <div className="flex items-center gap-2 sm:justify-end">
            <span className="text-[var(--landing-brand-soft)]">
              <IconShield />
            </span>
            <span>Audited operational support only</span>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-40 border-b border-[var(--landing-border)] bg-[var(--landing-nav-bg)] backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <a href="/landing" className="flex items-center gap-2">
            <span className="rounded-full bg-[var(--landing-brand)] p-2 text-[var(--landing-on-dark)]">
              <IconBolt />
            </span>
            <span className="text-lg font-bold tracking-wide">VetCan</span>
          </a>
          <nav className="hidden items-center gap-7 text-sm font-semibold md:flex">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-[var(--landing-muted)] transition hover:text-[var(--landing-brand)]"
              >
                {link.label}
              </a>
            ))}
          </nav>
          <a
            href="#demo"
            className="inline-flex items-center gap-2 rounded-[var(--landing-radius-pill)] bg-[var(--landing-brand)] px-4 py-2 text-sm font-semibold text-[var(--landing-on-dark)] shadow-[var(--landing-shadow-button)] transition hover:bg-[var(--landing-brand-dark)]"
          >
            Request a Demo
            <ArrowRight />
          </a>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-gradient-to-br from-[var(--landing-hero-from)] via-[var(--landing-hero-via)] to-[var(--landing-hero-to)] text-[var(--landing-on-dark)]">
          <div className="absolute -left-28 top-8 h-64 w-64 rounded-full bg-[var(--landing-glow-a)] blur-3xl" />
          <div className="absolute -bottom-24 right-0 h-72 w-72 rounded-full bg-[var(--landing-glow-b)] blur-3xl" />

          <div className="relative mx-auto grid max-w-6xl gap-10 px-4 pb-24 pt-16 lg:grid-cols-[1fr,0.95fr] lg:pb-28 lg:pt-24">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[var(--landing-brand-soft)]">
                VetCan Front Desk Automation
              </p>
              <h1 className="mt-4 max-w-2xl text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
                Book more visits on first contact.
              </h1>
              <p className="mt-4 max-w-2xl text-xl font-semibold text-[var(--landing-on-dark-soft)]">
                Front desk automation that saves payroll hours and helps protect
                roughly $50k/year in missed bookings and renewals.
              </p>
              <p className="mt-5 max-w-2xl text-base text-[var(--landing-on-dark-soft)] sm:text-lg">
                Event-driven automation, audited workflows, staff handoff,
                scheduling support, and renewal reminders built for busy
                veterinary clinics.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="#demo"
                  className="inline-flex items-center gap-2 rounded-[var(--landing-radius-pill)] bg-[var(--landing-surface)] px-6 py-3 text-sm font-semibold text-[var(--landing-brand-deep)] shadow-[var(--landing-shadow-button)] transition hover:bg-[var(--landing-surface-soft)]"
                >
                  Request a Demo
                  <ArrowRight />
                </a>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center gap-2 rounded-[var(--landing-radius-pill)] border border-[var(--landing-soft-border-on-dark)] px-6 py-3 text-sm font-semibold text-[var(--landing-on-dark)] transition hover:border-[var(--landing-on-dark)]"
                >
                  See How It Works
                </a>
              </div>
              <p className="mt-5 text-sm text-[var(--landing-on-dark-soft)]">
                Operational assistance only. No diagnosis or medical advice.
              </p>
            </div>

            <div className="self-center rounded-[var(--landing-radius-panel)] border border-[var(--landing-glass-border)] bg-[var(--landing-glass)] p-6 shadow-[var(--landing-shadow-soft)] backdrop-blur">
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[var(--landing-brand-soft)]">
                Today&apos;s Workflow Snapshot
              </p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="rounded-[var(--landing-radius-inner)] bg-[var(--landing-glass-strong)] p-4 text-[var(--landing-ink)]">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--landing-muted)]">
                    Booking Exceptions Queue
                  </p>
                  <p className="mt-2 text-2xl font-bold">0 missed</p>
                  <p className="mt-1 text-sm text-[var(--landing-ink-soft)]">
                    Manual exceptions assigned and tracked.
                  </p>
                </div>
                <div className="rounded-[var(--landing-radius-inner)] bg-[var(--landing-glass-strong)] p-4 text-[var(--landing-ink)]">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--landing-muted)]">
                    Renewal Nudges
                  </p>
                  <p className="mt-2 text-2xl font-bold">Automated</p>
                  <p className="mt-1 text-sm text-[var(--landing-ink-soft)]">
                    Timed reminders reduce manual outreach.
                  </p>
                </div>
              </div>
              <div className="mt-4 space-y-2 rounded-[var(--landing-radius-inner)] bg-[var(--landing-glass-strong)] p-4 text-sm text-[var(--landing-ink-soft)]">
                <div className="flex items-center gap-2">
                  <span className="text-[var(--landing-brand)]">
                    <IconShield />
                  </span>
                  Full audit trail for every operational action
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[var(--landing-brand)]">
                    <IconClipboard />
                  </span>
                  Human handoff with context when staff is needed
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[var(--landing-brand)]">
                    <IconBolt />
                  </span>
                  Workflow triggers across phone, SMS, and scheduling events
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="how-it-works"
          className="relative z-10 mx-auto -mt-14 max-w-6xl px-4 sm:-mt-16"
        >
          <div className="rounded-[var(--landing-radius-panel)] border border-[var(--landing-border)] bg-[var(--landing-surface)] p-6 shadow-[var(--landing-shadow-soft)] sm:p-8">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-bold sm:text-4xl">How It Works</h2>
              <p className="mt-3 text-[var(--landing-muted)]">
                Four operational steps that keep bookings, appointments, and
                renewals moving without extra front desk overhead.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {steps.map((step, index) => (
                <article
                  key={step.title}
                  className="rounded-[var(--landing-radius-card)] border border-[var(--landing-border)] bg-[var(--landing-surface-muted)] p-5 shadow-[var(--landing-shadow-card)]"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--landing-brand)] text-[var(--landing-on-dark)]">
                      {iconFor(step.icon)}
                    </span>
                    <span className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--landing-muted)]">
                      Step {index + 1}
                    </span>
                  </div>
                  <h3 className="mt-4 text-lg font-bold">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--landing-muted)]">
                    {step.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="benefits"
          className="mx-auto max-w-6xl px-4 pb-20 pt-16 sm:pt-20"
        >
          <div className="max-w-3xl">
            <h2 className="text-3xl font-bold sm:text-4xl">We Put Clinics First</h2>
            <p className="mt-3 text-[var(--landing-muted)]">
              VetCan is built for operations: fewer misses, less phone tag, and
              clear accountability across every handoff.
            </p>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {benefits.map((benefit) => (
              <article
                key={benefit.title}
                className="rounded-[var(--landing-radius-card)] border border-[var(--landing-border)] bg-[var(--landing-surface)] p-6 shadow-[var(--landing-shadow-card)]"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[var(--landing-brand-soft)] text-[var(--landing-brand-deep)]">
                  {iconFor(benefit.icon)}
                </div>
                <h3 className="mt-4 text-xl font-bold">{benefit.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[var(--landing-muted)]">
                  {benefit.description}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section
          id="testimonials"
          className="border-y border-[var(--landing-border)] bg-[var(--landing-surface-muted)] py-20"
        >
          <div className="mx-auto max-w-6xl px-4">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-bold sm:text-4xl">Testimonials</h2>
              <p className="mt-3 text-[var(--landing-muted)]">
                Teams using VetCan report faster follow-up execution, clearer
                handoffs, and stronger renewal consistency.
              </p>
            </div>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {testimonials.map((testimonial) => (
                <article
                  key={testimonial.name}
                  className="rounded-[var(--landing-radius-card)] border border-[var(--landing-border)] bg-[var(--landing-surface)] p-6 shadow-[var(--landing-shadow-card)]"
                >
                  <p className="text-[15px] leading-relaxed text-[var(--landing-ink-soft)]">
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>
                  <div className="mt-5 border-t border-[var(--landing-border)] pt-4">
                    <p className="font-bold">{testimonial.name}</p>
                    <p className="text-sm text-[var(--landing-muted)]">
                      {testimonial.role}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="mx-auto max-w-6xl px-4 py-20">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-bold sm:text-4xl">FAQ</h2>
            <p className="mt-3 text-[var(--landing-muted)]">
              Common rollout and compliance questions from clinic operators.
            </p>
          </div>
          <div className="mt-8 space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={faq.question}
                open={index === 0}
                className="group rounded-[var(--landing-radius-card)] border border-[var(--landing-border)] bg-[var(--landing-surface)] px-5 py-4 shadow-[var(--landing-shadow-card)]"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold">
                  <span>{faq.question}</span>
                  <ChevronDown />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-[var(--landing-muted)]">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </section>

        <section id="demo" className="bg-[var(--landing-brand)] py-14 text-[var(--landing-on-dark)]">
          <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[var(--landing-brand-soft)]">
                Get Started Today
              </p>
              <h2 className="mt-2 text-3xl font-bold sm:text-4xl">
                Cut front desk overload and protect revenue this quarter.
              </h2>
              <p className="mt-3 text-[var(--landing-on-dark-soft)]">
                Request a walkthrough to map first-contact booking, operational follow-up, and renewal
                workflows for your clinic.
              </p>
            </div>
            <a
              href="mailto:hello@vetcan.io?subject=VetCan%20Demo%20Request"
              className="inline-flex items-center justify-center gap-2 rounded-[var(--landing-radius-pill)] bg-[var(--landing-surface)] px-6 py-3 text-sm font-semibold text-[var(--landing-brand-deep)] shadow-[var(--landing-shadow-button)] transition hover:bg-[var(--landing-surface-soft)]"
            >
              Request a Demo
              <ArrowRight />
            </a>
          </div>
        </section>
      </main>

      <footer className="bg-[var(--landing-footer)] text-[var(--landing-footer-muted)]">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 md:grid-cols-[1.2fr,1fr,1fr]">
          <div>
            <p className="text-lg font-bold text-[var(--landing-on-dark)]">VetCan</p>
            <p className="mt-2 text-sm">
              Event-driven operational automation for veterinary clinics.
              Scheduling support, first-contact booking, and renewal workflow
              tracking with audited execution.
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.12em]">
              Product
            </p>
            <ul className="mt-3 space-y-2 text-sm">
              {navLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="transition hover:text-[var(--landing-on-dark)]"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.12em]">
              Company
            </p>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <a href="#" className="transition hover:text-[var(--landing-on-dark)]">
                  Privacy
                </a>
              </li>
              <li>
                <a href="#" className="transition hover:text-[var(--landing-on-dark)]">
                  Security
                </a>
              </li>
              <li>
                <a href="#" className="transition hover:text-[var(--landing-on-dark)]">
                  Contact
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[var(--landing-footer-border)] py-4 text-center text-xs">
          Operational assistance only. No diagnosis or medical advice.
        </div>
      </footer>
    </div>
  );
}
